// Most of the time you visit this file would be conditionally rendering tab and subTabs
// Please refer `conditionallyMergedChildRoutes` and `currentRoutes` where we can conditionally render tab and subTab

import { useAppSelector, useLocation } from 'src/utils/Hooks';
import { TabRoute, TabRouteWithoutIndex } from 'src/components/CommonV2Components/GenericSideBar/types';
import { buildAtlasV2TabAndSubtabLink } from 'src/components/AtlasV2Components/AtlasV2LayoutComponents/Header/AtlasV2SubtabNav';
import { showContentAndRatings } from 'src/store/modules/user/selectors';
import { buildTabAndSubtabLink } from 'src/components/BeaconV2Components/SubtabNav/SubtabNavigation';
import { findCurrentTabAndSubtab } from 'src/components/CommonV2Components/GenericSideBar/GenericSideBarUtils';
import {
  getAppSubscriptionTier,
  getAppName,
  getTrafficV2Treatment,
  getAtlasSalesV2Treatment,
  getAtlasConversionV2Treatment,
  shouldShowMicroTrends
} from 'src/utils/app';
import { SidebarLabel } from 'src/components/BeaconV2Components/GenericSidebarNav/GenericSidebar';
import { AppName } from 'sl-api-connector';

import React from 'react';
import { useCategoryEntity } from 'src/utils/Hooks/reduxSelectors';

export enum ATLAS_TABS {
  SUMMARY = 'scorecard',
  SUMMARY_V2 = 'scorecardV2',
  SALES = 'sales',
  SALES_V2 = 'salesV2',
  TRAFFIC = 'traffic',
  TRAFFIC_V2 = 'trafficV2',
  PRICES = 'prices',
  PRICES_V2 = 'pricesV2',
  CONTENT = 'content',
  CONTENT_V2 = 'contentV2',
  RATING = 'rating',
  RATING_V2 = 'ratingV2',
  CONVERSION = 'conversion',
  CONVERSION_V2 = 'conversionV2'
}

export enum ATLAS_SUBTABS {
  KEY_METRICS = 'all',
  MICRO_TRENDS = 'microtrends',
  TOTAL_CLICKS = 'totalClicks',
  ORGANIC_CLICKS = 'organicClicks',
  PURCHASE_RATE = 'purchaseRate',
  AD_CLICKS = 'adClicks',
  RETAIL_PRICE = 'retailPrice',
  PROMOTIONS = 'promotions',
  CONTENT_SCORE = 'contentScore',
  RATING = 'rating',
  RETAIL_SALES = 'retailSales',
  UNITS_SOLD = 'unitsSold',
  OTHER = 'otherClicks'
}

/**
 * @param location the location object from react router
 * @returns a collection of all tab routes and the current route that is active for use in AppLinkTabs
 */
export const useAtlasV2Routes = () => {
  const location = useLocation();
  const app = useAppSelector((state) => state.app);
  const entityType = useAppSelector((state) => state?.entityService?.mainEntity?.type);
  const { isAllCategoriesEntity } = useCategoryEntity();
  const appName = getAppName();

  const requireSubscription = getAppSubscriptionTier() !== 'prime';
  const defaultTabRoutes: TabRouteWithoutIndex[] = [
    ...(getAtlasSalesV2Treatment() !== 'on'
      ? [
          {
            label: 'Summary',
            route: buildAtlasV2TabAndSubtabLink({
              app,
              location,
              tab: ATLAS_TABS.SUMMARY,
              subtab: ATLAS_SUBTABS.KEY_METRICS
            }),
            childSubtabs: [{ label: 'Key Metrics', value: ATLAS_SUBTABS.KEY_METRICS, parentTab: ATLAS_TABS.SUMMARY }]
          }
        ]
      : []),

    ...(getAtlasSalesV2Treatment() !== 'off'
      ? [
          {
            label: 'Summary',
            route: buildAtlasV2TabAndSubtabLink({
              app,
              location,
              tab: ATLAS_TABS.SUMMARY_V2,
              subtab: ATLAS_SUBTABS.KEY_METRICS
            }),
            childSubtabs: [
              { label: 'Key Metrics', value: ATLAS_SUBTABS.KEY_METRICS, parentTab: ATLAS_TABS.SUMMARY_V2 },
              ...((entityType === 'category' || entityType === 'microtrend' || entityType === 'subcategory') &&
              shouldShowMicroTrends()
                ? [
                    {
                      label: entityType === 'microtrend' ? 'Details' : 'Microtrends',
                      value: ATLAS_SUBTABS.MICRO_TRENDS,
                      parentTab: ATLAS_TABS.SUMMARY_V2
                    }
                  ]
                : [])
            ]
          }
        ]
      : []),

    ...(getTrafficV2Treatment() !== 'on'
      ? [
          {
            requireSubscription,
            label: requireSubscription ? (
              <SidebarLabel requireSubscription={requireSubscription} label="Traffic" />
            ) : (
              'Traffic'
            ),
            route: buildAtlasV2TabAndSubtabLink({
              app,
              location,
              tab: ATLAS_TABS.TRAFFIC,
              subtab: ATLAS_SUBTABS.TOTAL_CLICKS
            }),
            childSubtabs: [
              { label: 'Total', value: ATLAS_SUBTABS.TOTAL_CLICKS, parentTab: ATLAS_TABS.TRAFFIC },
              { label: 'Organic', value: ATLAS_SUBTABS.ORGANIC_CLICKS, parentTab: ATLAS_TABS.TRAFFIC },
              { label: 'Paid', value: ATLAS_SUBTABS.AD_CLICKS, parentTab: ATLAS_TABS.TRAFFIC }
            ]
          }
        ]
      : []),
    ...(getTrafficV2Treatment() !== 'off'
      ? [
          {
            requireSubscription,
            label: requireSubscription ? (
              <SidebarLabel requireSubscription={requireSubscription} label="Traffic" />
            ) : (
              'Traffic'
            ),
            route: buildAtlasV2TabAndSubtabLink({
              app,
              location,
              tab: ATLAS_TABS.TRAFFIC_V2,
              subtab: ATLAS_SUBTABS.TOTAL_CLICKS
            }),
            childSubtabs: [
              { label: 'Total', value: ATLAS_SUBTABS.TOTAL_CLICKS, parentTab: ATLAS_TABS.TRAFFIC_V2 },
              { label: 'Organic', value: ATLAS_SUBTABS.ORGANIC_CLICKS, parentTab: ATLAS_TABS.TRAFFIC_V2 },
              { label: 'Other', value: ATLAS_SUBTABS.OTHER, parentTab: ATLAS_TABS.TRAFFIC_V2 },
              { label: 'Paid Search', value: ATLAS_SUBTABS.AD_CLICKS, parentTab: ATLAS_TABS.TRAFFIC_V2 }
            ]
          }
        ]
      : []),

    ...(getAtlasConversionV2Treatment() !== 'on'
      ? [
          {
            requireSubscription,
            label: requireSubscription ? (
              <SidebarLabel requireSubscription={requireSubscription} label="Conversion" />
            ) : (
              'Conversion'
            ),
            route: buildAtlasV2TabAndSubtabLink({
              app,
              location,
              tab: ATLAS_TABS.CONVERSION,
              subtab: ATLAS_SUBTABS.PURCHASE_RATE
            }),
            childSubtabs: [{ label: 'Summary', value: ATLAS_SUBTABS.PURCHASE_RATE, parentTab: ATLAS_TABS.CONVERSION }]
          }
        ]
      : []),

    ...(getAtlasSalesV2Treatment() !== 'off'
      ? [
          {
            requireSubscription,
            label: requireSubscription ? (
              <SidebarLabel requireSubscription={requireSubscription} label="Conversion" />
            ) : (
              'Conversion'
            ),
            route: buildAtlasV2TabAndSubtabLink({
              app,
              location,
              tab: ATLAS_TABS.CONVERSION_V2,
              subtab: ATLAS_SUBTABS.PURCHASE_RATE
            }),
            childSubtabs: [
              { label: 'Summary', value: ATLAS_SUBTABS.PURCHASE_RATE, parentTab: ATLAS_TABS.CONVERSION_V2 }
            ]
          }
        ]
      : []),

    ...(getAtlasSalesV2Treatment() !== 'on'
      ? [
          {
            label: 'Sales',
            route: buildAtlasV2TabAndSubtabLink({
              app,
              location,
              tab: ATLAS_TABS.SALES,
              subtab: ATLAS_SUBTABS.RETAIL_SALES
            }),
            childSubtabs: [
              { label: 'Retail Sales', value: ATLAS_SUBTABS.RETAIL_SALES, parentTab: ATLAS_TABS.SALES },
              { label: 'Units Sold', value: ATLAS_SUBTABS.UNITS_SOLD, parentTab: ATLAS_TABS.SALES }
            ]
          }
        ]
      : []),

    ...(getAtlasSalesV2Treatment() !== 'off'
      ? [
          {
            label: 'Sales',
            route: buildAtlasV2TabAndSubtabLink({
              app,
              location,
              tab: ATLAS_TABS.SALES_V2,
              subtab: ATLAS_SUBTABS.RETAIL_SALES
            }),
            childSubtabs: [
              { label: 'Retail Sales', value: ATLAS_SUBTABS.RETAIL_SALES, parentTab: ATLAS_TABS.SALES_V2 },
              { label: 'Units Sold', value: ATLAS_SUBTABS.UNITS_SOLD, parentTab: ATLAS_TABS.SALES_V2 }
            ]
          }
        ]
      : []),

    ...(getAtlasSalesV2Treatment() !== 'on'
      ? [
          {
            label: 'Prices',
            route: buildAtlasV2TabAndSubtabLink({
              app,
              location,
              tab: ATLAS_TABS.PRICES,
              subtab: ATLAS_SUBTABS.RETAIL_PRICE
            }),
            childSubtabs: [
              { label: 'Summary', value: ATLAS_SUBTABS.RETAIL_PRICE, parentTab: ATLAS_TABS.PRICES },
              { label: 'Promotions', value: ATLAS_SUBTABS.PROMOTIONS, parentTab: ATLAS_TABS.PRICES }
            ]
          }
        ]
      : []),

    ...(getAtlasSalesV2Treatment() !== 'off'
      ? [
          {
            label: 'Prices',
            route: buildAtlasV2TabAndSubtabLink({
              app,
              location,
              tab: ATLAS_TABS.PRICES_V2,
              subtab: ATLAS_SUBTABS.RETAIL_PRICE
            }),
            childSubtabs: [
              { label: 'Summary', value: ATLAS_SUBTABS.RETAIL_PRICE, parentTab: ATLAS_TABS.PRICES_V2 },
              { label: 'Promotions', value: ATLAS_SUBTABS.PROMOTIONS, parentTab: ATLAS_TABS.PRICES_V2 }
            ]
          }
        ]
      : []),

    ...(showContentAndRatings() && getAtlasSalesV2Treatment() !== 'on'
      ? [
          {
            label: 'Content',
            route: buildAtlasV2TabAndSubtabLink({
              app,
              location,
              tab: ATLAS_TABS.CONTENT,
              subtab: ATLAS_SUBTABS.CONTENT_SCORE
            }),
            childSubtabs: [{ label: 'Utilization', value: ATLAS_SUBTABS.CONTENT_SCORE, parentTab: ATLAS_TABS.CONTENT }]
          }
        ]
      : []),

    ...(showContentAndRatings() && getAtlasSalesV2Treatment() !== 'off'
      ? [
          {
            label: 'Content',
            route: buildAtlasV2TabAndSubtabLink({
              app,
              location,
              tab: ATLAS_TABS.CONTENT_V2,
              subtab: ATLAS_SUBTABS.CONTENT_SCORE
            }),
            childSubtabs: [
              { label: 'Utilization', value: ATLAS_SUBTABS.CONTENT_SCORE, parentTab: ATLAS_TABS.CONTENT_V2 }
            ]
          }
        ]
      : []),

    ...(showContentAndRatings() && getAtlasSalesV2Treatment() !== 'on'
      ? [
          {
            label: 'Ratings',
            route: buildAtlasV2TabAndSubtabLink({
              app,
              location,
              tab: ATLAS_TABS.RATING,
              subtab: ATLAS_SUBTABS.RATING
            }),
            childSubtabs: [{ label: 'Summary', value: ATLAS_SUBTABS.RATING, parentTab: ATLAS_TABS.RATING }]
          }
        ]
      : []),
    ...(showContentAndRatings() && getAtlasSalesV2Treatment() !== 'off'
      ? [
          {
            label: 'Ratings',
            route: buildAtlasV2TabAndSubtabLink({
              app,
              location,
              tab: ATLAS_TABS.RATING_V2,
              subtab: ATLAS_SUBTABS.RATING
            }),
            childSubtabs: [{ label: 'Summary', value: ATLAS_SUBTABS.RATING, parentTab: ATLAS_TABS.RATING_V2 }]
          }
        ]
      : [])
  ];

  const topicsTabRoutes: TabRouteWithoutIndex[] = [
    {
      label: 'Traffic',
      tabMappedFrom: ATLAS_TABS.TRAFFIC,
      subtabMappedFrom: ATLAS_SUBTABS.TOTAL_CLICKS,
      route: buildTabAndSubtabLink({ app, location, tab: ATLAS_TABS.TRAFFIC, subtab: ATLAS_SUBTABS.TOTAL_CLICKS }),
      childSubtabs: [
        { label: 'Total', value: ATLAS_SUBTABS.TOTAL_CLICKS, parentTab: ATLAS_TABS.TRAFFIC },
        { label: 'Organic', value: ATLAS_SUBTABS.ORGANIC_CLICKS, parentTab: ATLAS_TABS.TRAFFIC },
        { label: 'Paid Search', value: ATLAS_SUBTABS.AD_CLICKS, parentTab: ATLAS_TABS.TRAFFIC }
      ]
    }
  ];

  const defaultTabRoutesWithIndexes: TabRoute[] = defaultTabRoutes.map((route, index) => ({
    ...route,
    index
  }));

  const topicsTabRoutesWithIndexes: TabRoute[] = topicsTabRoutes.map((route, index) => ({ ...route, index }));

  const tabRoutesByEntityType = {
    searchtermlist: topicsTabRoutesWithIndexes,
    topic: topicsTabRoutesWithIndexes,
    topicgroup: topicsTabRoutesWithIndexes,
    default: defaultTabRoutesWithIndexes
  };

  if (appName !== AppName.Atlas) {
    return {
      routes: {
        defaultRoutes: [],
        topicsRoutes: []
      },
      currentRoute: null,
      getCurrentRoutes: () => [],
      currentSubtab: ''
    };
  }

  const { currentRoute, currentSubtab }: { currentRoute: TabRoute; currentSubtab: string } = findCurrentTabAndSubtab({
    location,
    childRoutes: tabRoutesByEntityType[entityType] || tabRoutesByEntityType.default
  });

  return {
    routes: {
      defaultRoutes: defaultTabRoutesWithIndexes,
      topicsRoutes: topicsTabRoutesWithIndexes
    },
    currentRoute,
    getCurrentRoutes: () => {
      return tabRoutesByEntityType[entityType] || tabRoutesByEntityType.default;
    },
    currentSubtab
  };
};
